import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { UserPlus, X, Search, User, Plus, Trash2 } from 'lucide-react';
import { api } from '../../../services/api';
import { enqueueSnackbar } from 'notistack';
import { useAuth } from '../../../contexts/AuthContext';
import { Militar, GrupoCargo, Grupo } from '../../../types/grupos';

interface GrupoMembrosModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
  grupo: Grupo | null;
  gruposCargos: GrupoCargo[];
  openDirectlyInAddMode?: boolean;
  isResponsavel?: boolean;
  ordemCargoAdmin?: number;
}

interface MemberEntry {
  key: string;
  id_militar: string;
  id_grupo_cargo: string;
  status: string;
  searchTerm: string;
  selectedMilitarNick: string;
  isDropdownOpen: boolean;
  militaresFiltrados: Militar[];
  searching: boolean;
}

let entryCounter = 0;
const createEntry = (): MemberEntry => ({
  key: String(++entryCounter),
  id_militar: '',
  id_grupo_cargo: '',
  status: 'A',
  searchTerm: '',
  selectedMilitarNick: '',
  isDropdownOpen: false,
  militaresFiltrados: [],
  searching: false,
});

const GrupoMembrosModal: React.FC<GrupoMembrosModalProps> = ({
  isOpen,
  onClose,
  onUpdate,
  grupo,
  gruposCargos,
  openDirectlyInAddMode = false,
  isResponsavel = false,
  ordemCargoAdmin,
}) => {
  const [entries, setEntries] = useState<MemberEntry[]>([createEntry()]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const { militar: militarLogado } = useAuth();

  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const debounceRefs = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const cargosFiltrados = gruposCargos
    .filter(cargo => cargo.id_grupo === grupo?.id)
    .filter(cargo => !isResponsavel || (!cargo.admin && !cargo.responsavel))
    .filter(cargo => ordemCargoAdmin === undefined || (cargo.ordem ?? 0) >= ordemCargoAdmin);

  useEffect(() => {
    if (isOpen && grupo) {
      if (openDirectlyInAddMode) setShowForm(true);
    } else {
      setEntries([createEntry()]);
      setShowForm(false);
    }
  }, [isOpen, grupo, openDirectlyInAddMode]);

  // Fecha dropdowns ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      setEntries(prev =>
        prev.map(entry => {
          const inputEl = inputRefs.current[entry.key];
          const dropdownEl = dropdownRefs.current[entry.key];
          if (
            inputEl && !inputEl.contains(event.target as Node) &&
            (!dropdownEl || !dropdownEl.contains(event.target as Node))
          ) {
            return { ...entry, isDropdownOpen: false };
          }
          return entry;
        })
      );
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const updateEntry = useCallback((key: string, changes: Partial<MemberEntry>) => {
    setEntries(prev => prev.map(e => e.key === key ? { ...e, ...changes } : e));
  }, []);

  const handleMilitarSearch = (key: string, value: string) => {
    updateEntry(key, {
      searchTerm: value,
      selectedMilitarNick: value,
      id_militar: '',
      isDropdownOpen: true,
    });

    if (debounceRefs.current[key]) clearTimeout(debounceRefs.current[key]);

    if (!value.trim()) {
      updateEntry(key, { militaresFiltrados: [], searching: false });
      return;
    }

    updateEntry(key, { searching: true });

    debounceRefs.current[key] = setTimeout(async () => {
      try {
        const response = await api.get(`/militares?nick=${encodeURIComponent(value)}`);
        const list: Militar[] = response.data.militares || [];
        updateEntry(key, { militaresFiltrados: list, searching: false });
      } catch (err) {
        console.error('Erro ao buscar militares:', err);
        updateEntry(key, { searching: false });
      }
    }, 300);
  };

  const handleFocusInput = (key: string) => {
    updateEntry(key, { isDropdownOpen: true });
  };

  const handleSelectMilitar = (key: string, militarId: string, militarNick: string) => {
    updateEntry(key, {
      id_militar: militarId,
      selectedMilitarNick: militarNick,
      searchTerm: militarNick,
      isDropdownOpen: false,
    });
  };

  const addEntry = () => setEntries(prev => [...prev, createEntry()]);

  const removeEntry = (key: string) => {
    setEntries(prev => prev.length === 1 ? prev : prev.filter(e => e.key !== key));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting || !grupo) return;

    const invalid = entries.find(e => !e.id_militar || !e.id_grupo_cargo);
    if (invalid) {
      enqueueSnackbar('Preencha o militar e o cargo em todas as linhas', { variant: 'warning' });
      return;
    }

    setIsSubmitting(true);
    try {
      const membros = entries.map(e => ({
        id_militar: e.id_militar,
        id_grupo: grupo.id,
        id_grupo_cargo: e.id_grupo_cargo,
        id_militar_adicionou: militarLogado?.id,
        status: e.status,
      }));

      const { data } = await api.post('/militares-grupos/batch', { membros });

      if (data.errors?.length > 0) {
        data.errors.forEach((err: { id_militar: string; message: string }) => {
          enqueueSnackbar(`Erro em um membro: ${err.message}`, { variant: 'error' });
        });
      }
      if (data.success?.length > 0) {
        enqueueSnackbar(
          `${data.success.length} membro(s) adicionado(s) com sucesso!`,
          { variant: 'success' }
        );
        setEntries([createEntry()]);
        setShowForm(false);
        onUpdate();
        if (!data.errors?.length) onClose();
      }
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Erro ao adicionar membros ao grupo', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!grupo) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Adicionar Membros - {grupo.grupo}
            </DialogTitle>
            <DialogDescription>
              Adicione um ou mais membros ao grupo de uma só vez.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {!showForm && (
              <Button
                onClick={() => setShowForm(true)}
                className="w-full"
                variant="outline"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Adicionar Membros
              </Button>
            )}

            {showForm && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-3">
                  {entries.map((entry, index) => (
                    <div
                      key={entry.key}
                      className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end p-3 border border-border rounded-md bg-muted/20"
                    >
                      {/* Militar */}
                      <div className="relative">
                        <Label className="text-xs mb-1 block">Militar *</Label>
                        <div className="relative">
                          <input
                            ref={el => { inputRefs.current[entry.key] = el; }}
                            type="text"
                            value={entry.selectedMilitarNick}
                            onChange={e => handleMilitarSearch(entry.key, e.target.value)}
                            onFocus={() => handleFocusInput(entry.key)}
                            placeholder="Digite o nick..."
                            className="w-full px-3 py-2 pr-9 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                          />
                          <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                        </div>
                        {/* Dropdown de militares */}
                        {entry.isDropdownOpen && (
                          <div
                            ref={el => { dropdownRefs.current[entry.key] = el; }}
                            className="absolute left-0 right-0 top-full mt-1 z-[100] bg-background border border-border rounded-md shadow-lg max-h-60 overflow-y-auto"
                          >
                            {entry.searching ? (
                              <div className="px-3 py-2 text-center text-sm text-muted-foreground">
                                Buscando...
                              </div>
                            ) : entry.militaresFiltrados.length > 0 ? (
                              <>
                                {entry.militaresFiltrados.slice(0, 10).map(militar => (
                                  <button
                                    key={militar.id}
                                    type="button"
                                    onMouseDown={e => e.preventDefault()}
                                    onClick={() => handleSelectMilitar(entry.key, militar.id, militar.nick)}
                                    className="w-full px-3 py-2 text-left text-foreground hover:bg-muted focus:bg-muted focus:outline-none cursor-pointer"
                                  >
                                    <div className="flex items-center gap-2">
                                      <User className="w-4 h-4 text-muted-foreground" />
                                      <span>{militar.patente?.abreviacao} {militar.nick}</span>
                                    </div>
                                  </button>
                                ))}
                                {entry.militaresFiltrados.length > 10 && (
                                  <div className="px-3 py-2 text-sm text-muted-foreground border-t border-border">
                                    {entry.militaresFiltrados.length - 10} mais resultados...
                                  </div>
                                )}
                              </>
                            ) : entry.searchTerm ? (
                              <div className="p-3 text-center text-muted-foreground">
                                <User className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
                                <p>Nenhum militar encontrado</p>
                                <p className="text-sm">Tente outro nick</p>
                              </div>
                            ) : null}
                          </div>
                        )}
                      </div>

                      {/* Cargo */}
                      <div>
                        <Label className="text-xs mb-1 block">Cargo *</Label>
                        <select
                          value={entry.id_grupo_cargo}
                          onChange={e => updateEntry(entry.key, { id_grupo_cargo: e.target.value })}
                          className="flex h-10 w-full appearance-none rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                          required
                        >
                          <option value="">Selecione um cargo</option>
                          {cargosFiltrados.map(cargo => (
                            <option key={cargo.id} value={cargo.id}>
                              {cargo.cargo}{cargo.admin ? ' (Admin)' : ''}{cargo.responsavel ? ' (Responsável)' : ''}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Remover linha */}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10 self-end"
                        onClick={() => removeEntry(entry.key)}
                        disabled={entries.length === 1}
                        title="Remover linha"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>

                {/* Botão adicionar mais uma linha */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addEntry}
                  className="w-full border-dashed"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar mais um membro
                </Button>

                <div className="flex justify-end space-x-2 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={onClose}>
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting
                      ? 'Adicionando...'
                      : entries.length === 1
                        ? 'Adicionar Membro'
                        : `Adicionar ${entries.length} Membros`}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GrupoMembrosModal;
